import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faTimes,
  faSpinner,
  faChevronDown,
  faChevronUp,
  faSearch,
  faEye,
  faPaperclip,
  faSave,
  faPaperPlane,
  faTimesCircle,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faUndo,
  faEyeSlash,
  faCamera, // Added for Scan button
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import ClaimDetailsModal from "./components/ClaimDetailsModal";
import AttachFilesModal from "./components/AttachFilesModal";
import ConfirmSubmitModal from "./components/ConfirmSubmitModal";
import { useDropzone } from "react-dropzone";

// Possible Claim Statuses
const claimStatusStyles = {
  NULL: "bg-gray-100 text-gray-800",
  "For Processing": "bg-blue-100 text-blue-800",
  Processed: "bg-green-100 text-green-800",
  Extracted: "bg-yellow-100 text-yellow-800",
  Incomplete: "bg-red-100 text-red-800",
  Paid: "bg-purple-100 text-purple-800",
};

const AddClaimsBatch = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    batch_no: "",
    batch_status: "Draft",
    received_date: "",
    due_date: "",
    provider_code: "",
    provider_reference_no: "",
    soa_amount: "",
    accepted_by: "",
    actual_claim_count: 0,
    actual_claim_amount: 0,
    batch_type: "Claims",
    est_claim_count: 0,
    est_claim_amount: 0,
    created_by: "system_user", // Replace with auth user
    physician_code: null,
    remarks: "",
    soa_fileupload: "[]",
  });
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingText, setIsProcessingText] = useState("Saving Batch...");
  const [isStep1Visible, setIsStep1Visible] = useState(true);
  const [providers, setProviders] = useState([]);
  const [providerPaymentTerms, setProviderPaymentTerms] = useState(30);
  const [isBatchSaved, setIsBatchSaved] = useState(false);
  const fileInputRef = useRef(null);
  const isMounted = useRef(true);

  // added for Scan Docs features
  // const [claims, setClaims] = useState([]); // Claims in the batch
  // const [loading, setLoading] = useState(false);
  // const [batchNo, setBatchNo] = useState(''); // Batch number
  // const [attachedFiles, setAttachedFiles] = useState({}); // Map of transaction_no to attached files
  // const [scanning, setScanning] = useState(false); // New state for scanning status

  // Track if there are unsaved changes
  const hasUnsavedChanges = useRef(false);

  // Search Claims State
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showingEntries, setShowingEntries] = useState("");
  const [filters, setFilters] = useState({
    loa_no: "",
    transaction_no: "",
    patient_name: "",
    coverage: "",
    claim_status: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [attachedClaims, setAttachedClaims] = useState([]);
  const [isAttachFilesModalOpen, setIsAttachFilesModalOpen] = useState(false);
  const [selectedClaimForFiles, setSelectedClaimForFiles] = useState(null);
  const [claimFiles, setClaimFiles] = useState([]);
  const claimFileInputRef = useRef(null);
  const [isDraggingClaim, setIsDraggingClaim] = useState(false);
  const [claims, setClaims] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);

  //
  // const [claims, setClaims] = useState([]); // Claims in the batch
  const [loading, setLoading] = useState(false);
  const [batchNo, setBatchNo] = useState(""); // Batch number
  const [attachedFiles, setAttachedFiles] = useState({}); // Map of transaction_no to attached files
  const [scanning, setScanning] = useState(false); // New state for scanning status

  // Existing functions (adjust based on your actual implementation)
  const handleAttachFiles = async (transactionNo, files) => {
    setLoading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append("file[]", file)); // Support multiple files
    formData.append("transaction_no", transactionNo);

    try {
      const response = await axios.post(
        `${BASE_URL}/claims/attach-files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const newFiles = response.data; // Adjust based on your API response
      setAttachedFiles((prev) => ({
        ...prev,
        [transactionNo]: [...(prev[transactionNo] || []), ...newFiles],
      }));
      toast.success("Files attached successfully!");
    } catch (error) {
      toast.error("Failed to attach files.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // New function to handle scanning
  // const handleScanFiles = (transactionNo) => {
  //   setScanning(true);
  //   window.scanner.scan(
  //     (success, response) => {
  //       if (!success) {
  //         setScanning(false);
  //         toast.error('Scanning failed: ' + (response.message || 'Unknown error'));
  //         if (response.message.includes('Asprise Scan Client')) {
  //           toast.error(
  //             'Please install the Asprise Scan Client: http://asprise.com/scan/client/download.php',
  //             { duration: 6000 }
  //           );
  //         }
  //         return;
  //       }

  //       const scannedImages = window.scanner.getScannedImages(response, true, false); // Get images
  //       if (scannedImages.length === 0) {
  //         setScanning(false);
  //         toast.error('No images scanned.');
  //         return;
  //       }

  //       // Convert scanned images to File objects for upload
  //       const files = scannedImages.map((img, index) => {
  //         const blob = dataURLtoBlob(img.src); // Convert data URL to Blob
  //         return new File([blob], `scanned_${transactionNo}_${index + 1}.${img.src.includes('jpeg') ? 'jpg' : 'png'}`, {
  //           type: img.src.includes('jpeg') ? 'image/jpeg' : 'image/png',
  //         });
  //       });

  //       // Call existing handleAttachFiles to upload scanned files
  //       handleAttachFiles(transactionNo, files);
  //       setScanning(false);
  //     },
  //     {
  //       source: 'scanner', // Use TWAIN/WIA scanner
  //       format: 'jpg', // Can also use 'png' or 'pdf'
  //       output_settings: [
  //         {
  //           type: 'return-base64', // Return images as base64 for processing
  //           format: 'jpg',
  //         },
  //       ],
  //     }
  //   );
  // };

  // Utility to convert data URL to Blob (for scanned images)
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Existing Dropzone component for manual uploads
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, _, event) => {
      const transactionNo = event.target.closest("tr")?.dataset.transactionNo; // Get transaction_no from table row
      if (transactionNo) {
        handleAttachFiles(transactionNo, acceptedFiles);
      }
    },
    accept: {
      "image/*": [".jpg", ".jpeg", ".png"],
      "application/pdf": [".pdf"],
    },
    multiple: true,
  });
  // added for

  // Add to existing state declarations
  const [dateFilters, setDateFilters] = useState({
    date_from: "",
    date_to: "",
  });

  // Initialize the date_from and date_to
  // set the date_from to 12 months before the current date and date_to to today’s date
  useEffect(() => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 12);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setDateFilters({
      date_from: formatDate(threeMonthsAgo),
      date_to: formatDate(today),
    });
  }, []);

  const [selectedProviderName, setSelectedProviderName] = useState("");

  // New State for Claim Details Modal
  const [isClaimDetailsModalOpen, setIsClaimDetailsModalOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);

  // New State for Confirm Submit Modal
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const allowedFileTypes = ["pdf", "jpg", "jpeg", "png"];
  const maxFileSizeMB = 10;
  const maxUploadLimit = 10;
  const phpFormatter = new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Table Columns
  const columns = [
    { key: "loa_no", label: "Loa No.", sortable: true },
    { key: "transaction_no", label: "Transaction No.", sortable: true },
    { key: "amount", label: "Amount", sortable: true },
    { key: "patient_name", label: "Patient Name", sortable: true },
    { key: "coverage", label: "Coverage", sortable: true },
    { key: "date", label: "Date", sortable: true },
    { key: "claim_status", label: "Claim Status", sortable: true },
    { key: "stale", label: "Stale?", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ];

  // Generate Batch Number on Mount
  // const generateBatchNumber = useCallback(async () => {
  //   if (!isMounted.current) return;
  //   try {
  //     setIsTableLoading(true);
  //     const response = await axios.post(
  //       `${BASE_URL}/claims/batch-header`,
  //       {
  //         _status: "Draft",
  //         _batch_no: null,
  //       },
  //       {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem(
  //             "authentication/token"
  //           )}`,
  //         },
  //       }
  //     );
  //     const { batch_no } = response.data;
  //     setFormData((prev) => ({ ...prev, batch_no }));
  //     toast.success(`Batch number ${batch_no} generated.`, { duration: 4000 });
  //   } catch (error) {
  //     toast.error("Failed to generate batch number.", { duration: 4000 });
  //   } finally {
  //     setIsTableLoading(false);
  //   }
  // }, []);

  // Generate Batch Number with Confirmation
  const generateBatchNumber = useCallback(async () => {
    if (!isMounted.current) return;

    // Show SweetAlert2 confirmation
    const result = await Swal.fire({
      title: "Generate New Batch Number",
      text: "Are you sure you want to generate a new batch number?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, generate",
      cancelButtonText: "No, cancel",
      buttonsStyling: false,
      customClass: {
        confirmButton:
          "bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mr-2",
        cancelButton:
          "bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400",
        popup: "rounded-lg",
        title: "text-lg font-semibold",
        htmlContainer: "text-sm",
      },
    });

    if (!result.isConfirmed) {
      // Redirect to /claims/batches on cancel
      // window.location.href = '/claims/batches';
      navigate("/claims/batches");
      return;
    }

    try {
      setIsTableLoading(true);
      const response = await axios.post(
        `${BASE_URL}/claims/batch-header`,
        {
          _status: "Draft",
          _batch_no: null,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      const { batch_no } = response.data;
      setFormData((prev) => ({ ...prev, batch_no }));
      toast.success(`Batch number ${batch_no} generated.`, { duration: 4000 });

      hasUnsavedChanges.current = true;
    } catch (error) {
      toast.error("Failed to generate batch number.", { duration: 4000 });
    } finally {
      setIsTableLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    generateBatchNumber();
    return () => {
      isMounted.current = false;
    };
  }, [generateBatchNumber]);

  // Handle browser back/forward navigation
  useEffect(() => {
    // Push a state to history to detect back navigation
    window.history.pushState(null, null, window.location.href);

    const handlePopState = (event) => {
      if (hasUnsavedChanges.current) {
        // Show a custom confirmation
        const confirmLeave = window.confirm(
          "You have unsaved changes. Do you want to save your progress before leaving?"
        );

        if (confirmLeave) {
          // Optionally save progress here
          saveProgress(); // Your save function
          // Allow navigation
          window.history.back();
        } else {
          // Prevent navigation by pushing the state again
          window.history.pushState(null, null, window.location.href);
        }
      } else {
        // No unsaved changes, allow navigation
        window.history.back();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // // Example function to simulate unsaved changes
  // const handleInputChange = () => {
  //   hasUnsavedChanges.current = true; // Set to true when user makes changes
  // };

  // // Example save function
  const saveProgress = () => {
    console.log("Saving progress...");
    hasUnsavedChanges.current = false; // Reset after saving
  };

  // Handle page unload (e.g., closing tab or navigating away)
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges.current) {
        // Show native browser prompt
        event.preventDefault();
        event.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return event.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Update Claim Counts
  useEffect(() => {
    const claimCount = attachedClaims.length;
    const claimAmount = attachedClaims.reduce(
      (sum, claim) => sum + (Number(claim.amount) || 0),
      0
    );
    setFormData((prev) => ({
      ...prev,
      actual_claim_count: claimCount,
      actual_claim_amount: claimAmount,
    }));
  }, [attachedClaims]);

  // Fetch providers
  const fetchProviders = async (search = "") => {
    try {
      setIsTableLoading(true);
      const response = await axios.get(
        `${BASE_URL}/providers/for-batch-registration`,
        {
          params: { search },
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setProviders(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch providers.", { duration: 4000 });
    } finally {
      setIsTableLoading(false);
    }
  };

  // Handle provider search
  const handleProviderSearch = (e) => {
    const value = e.target.value;
    if (!formData.received_date) {
      toast.error("Please enter Date Received before searching providers.", {
        duration: 4000,
      });
      return;
    }
    setSelectedProviderName(value);
    setFormData((prev) => ({ ...prev, provider_code: "" }));
    fetchProviders(value);
  };

  // Handle provider selection
  const handleProviderSelect = (provider) => {
    setFormData((prev) => ({ ...prev, provider_code: provider.provider_code }));
    setSelectedProviderName(provider.provider_name);
    setProviderPaymentTerms(provider.payment_terms || 30);
    setProviders([]);
    updateDueDate(formData.received_date, provider.payment_terms || 30);
  };

  // const handleDateFilterChange = (key, value) => {
  //   setDateFilters((prev) => {
  //     const newFilters = { ...prev, [key]: value };
  //     if (newFilters.date_from && newFilters.date_to) {
  //      		new Date(newFilters.date_to) < new Date(newFiles.date_from)
  //     }
  //     return newFilters;
  //   });
  // };

  // Update Due Date
  const updateDueDate = (receivedDate, paymentTerms) => {
    if (receivedDate) {
      const received = new Date(receivedDate);
      const dueDate = new Date(
        received.setDate(received.getDate() + paymentTerms)
      );
      setFormData((prev) => ({
        ...prev,
        due_date: dueDate.toISOString().split("T")[0],
      }));
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "received_date") {
        updateDueDate(value, providerPaymentTerms);
      }
      hasUnsavedChanges.current = true;
      return updated;
    });
  };

  // Handle SOA file selection
  const handleFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(
            ", "
          )} files allowed.`,
          { duration: 4000 }
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(
          `File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`,
          { duration: 4000 }
        );
        return false;
      }

      return true;
    });

    if (files.length + validFiles.length > maxUploadLimit) {
      toast.error(
        `You can only upload up to ${maxUploadLimit} files at a time.`,
        { duration: 4000 }
      );
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      uploadId: null,
      error: null,
      signed_url: null,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    newFiles.forEach((fileObj) => handleFileUpload(fileObj));
  };

  // Handle SOA file upload
  const handleFileUpload = async (fileObj) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f
      )
    );

    try {
      const soaFileformData = new FormData();
      soaFileformData.append("file", fileObj.file);
      soaFileformData.append("batch_no", formData.batch_no);

      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/upload-soa`,
        soaFileformData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file ? { ...f, progress } : f
              )
            );
          },
        }
      );

      const { upload_id, signed_url, file_name } = response.data;
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? {
                ...f,
                isUploading: false,
                progress: 100,
                uploadId: upload_id,
                signed_url,
              }
            : f
        )
      );
      setFormData((prev) => ({
        ...prev,
        soa_fileupload: JSON.stringify([
          ...JSON.parse(prev.soa_fileupload),
          { uploadId: upload_id, signed_url, file_name },
        ]),
      }));
      toast.success(`File ${fileObj.file.name} uploaded successfully!`, {
        duration: 4000,
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to upload file.";
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? { ...f, isUploading: false, error: errorMessage }
            : f
        )
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  // Handle SOA file removal
  const handleRemoveFile = async (fileObj) => {
    const result = await Swal.fire({
      title: "Remove File?",
      text: `Are you sure you want to remove ${fileObj.file.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove it",
    });

    if (result.isConfirmed) {
      setIsProcessing(true);
      setIsProcessingText("Deleting attached files...");
      try {
        await axios.delete(
          `${BASE_URL}/claims/batch-header/upload-soa/${fileObj.uploadId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        setFiles((prevFiles) =>
          prevFiles.filter((f) => f.file !== fileObj.file)
        );
        setFormData((prev) => ({
          ...prev,
          soa_fileupload: JSON.stringify(
            JSON.parse(prev.soa_fileupload).filter(
              (f) => f.uploadId !== fileObj.uploadId
            )
          ),
        }));
        toast.success("File removed successfully.", { duration: 3000 });
        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
      } catch (error) {
        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
        toast.error("Failed to remove file.", { duration: 4000 });
      }
    }
  };

  // Handle view SOA file
  const handleViewFile = (fileObj) => {
    if (fileObj.signed_url) {
      window.open(fileObj.signed_url, "_blank");
    } else {
      toast.error("File URL not available.", { duration: 4000 });
    }
  };

  // Handle claim file selection
  const handleClaimFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(
            ", "
          )} files allowed.`,
          { duration: 4000 }
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(
          `File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`,
          { duration: 4000 }
        );
        return false;
      }

      return true;
    });

    if (claimFiles.length + validFiles.length > maxUploadLimit) {
      toast.error(
        `You can only upload up to ${maxUploadLimit} files at a time.`,
        { duration: 4000 }
      );
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      uploadId: null,
      error: null,
      signed_url: null,
    }));

    setClaimFiles((prevFiles) => [...prevFiles, ...newFiles]);
    newFiles.forEach((fileObj) => handleClaimFileUpload(fileObj));
  };

  // Handle claim file upload
  const handleClaimFileUpload = async (fileObj) => {
    setClaimFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f
      )
    );

    try {
      const formData = new FormData();
      formData.append("file", fileObj.file);
      formData.append("transaction_no", selectedClaimForFiles.transaction_no);

      const response = await axios.post(
        `${BASE_URL}/claims/attach-files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setClaimFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file ? { ...f, progress } : f
              )
            );
          },
        }
      );

      const { upload_id, signed_url, file_name } = response.data;
      setClaimFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? {
                ...f,
                isUploading: false,
                progress: 100,
                uploadId: upload_id,
                signed_url,
              }
            : f
        )
      );
      toast.success(`File ${fileObj.file.name} uploaded successfully!`, {
        duration: 4000,
      });

      // Automatically save the uploaded file
      // await handleSaveClaimFiles();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to upload file.";
      setClaimFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? { ...f, isUploading: false, error: errorMessage }
            : f
        )
      );
      toast.error(errorMessage, { duration: 4000 });
    }
  };

  // Handle claim file removal
  const handleRemoveClaimFile = async (fileObj) => {
    const result = await Swal.fire({
      title: "Remove File?",
      text: `Are you sure you want to remove ${fileObj.file.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove it",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${BASE_URL}/claims/attach-files/${fileObj.uploadId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        setClaimFiles((prevFiles) =>
          prevFiles.filter((f) => f.file !== fileObj.file)
        );
        // Update attachedClaims to reflect the removed file
        setAttachedClaims((prevClaims) =>
          prevClaims.map((claim) =>
            claim.id === selectedClaimForFiles.id
              ? {
                  ...claim,
                  attachedFiles: claim.attachedFiles.filter(
                    (f) => f.uploadId !== fileObj.uploadId
                  ),
                }
              : claim
          )
        );
        toast.success("File removed successfully.", { duration: 3000 });
      } catch (error) {
        toast.error("Failed to remove file.", { duration: 4000 });
      }
    }
  };

  // Handle view claim file
  const handleViewClaimFile = (fileObj) => {
    if (fileObj.signed_url) {
      window.open(fileObj.signed_url, "_blank");
    } else {
      toast.error("File URL not available.", { duration: 4000 });
    }
  };

  // Save claim files
  // const handleSaveClaimFiles = async () => {
  //   if (claimFiles.some((f) => f.isUploading)) {
  //     toast.error("Please wait for all files to finish uploading.", {
  //       duration: 4000,
  //     });
  //     return;
  //   }
  //   if (claimFiles.length === 0) {
  //     toast.error("No files to save.", { duration: 4000 });
  //     return;
  //   }

  //   try {
  //     // Call API to activate files
  //     const uploadIds = claimFiles
  //       .filter((f) => f.uploadId)
  //       .map((f) => f.uploadId);
  //     await axios.post(
  //       `${BASE_URL}/claims/attach-files/activate`,
  //       { upload_ids: uploadIds },
  //       {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem(
  //             "authentication/token"
  //           )}`,
  //         },
  //       }
  //     );

  //     // Update local state
  //     setAttachedClaims((prevClaims) =>
  //       prevClaims.map((claim) =>
  //         claim.id === selectedClaimForFiles.id
  //           ? { ...claim, attachedFiles: claimFiles }
  //           : claim
  //       )
  //     );
  //     setClaimFiles([]);
  //     setIsAttachFilesModalOpen(false);
  //     toast.success("Claim files saved and activated successfully.", {
  //       duration: 4000,
  //     });
  //   } catch (error) {
  //     toast.error(
  //       error.response?.data?.message || "Failed to save claim files.",
  //       { duration: 4000 }
  //     );
  //   }
  // };

  const handleSaveClaimFiles = async () => {
    if (claimFiles.some((f) => f.isUploading)) {
      toast.error("Please wait for all files to finish uploading.", {
        duration: 4000,
      });
      return;
    }
    if (claimFiles.length === 0) {
      toast.error("No files to save.", { duration: 4000 });
      return;
    }
    if (claimFiles.some((f) => !f.uploadId)) {
      toast.error(
        "Some scanned files are not fully uploaded. Please try again.",
        { duration: 4000 }
      );
      return;
    }

    try {
      const uploadIds = claimFiles.map((f) => f.uploadId);
      await axios.post(
        `${BASE_URL}/claims/attach-files/activate`,
        { upload_ids: uploadIds },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      setAttachedClaims((prevClaims) =>
        prevClaims.map((claim) =>
          claim.id === selectedClaimForFiles.id
            ? {
                ...claim,
                attachedFiles: [...claim.attachedFiles, ...claimFiles],
              }
            : claim
        )
      );
      setClaimFiles([]);
      setIsAttachFilesModalOpen(false);
      toast.success("Claim files saved and activated successfully.", {
        duration: 4000,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save claim files.",
        { duration: 4000 }
      );
    }
  };

  // Handle drag-and-drop for SOA
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileChange(droppedFiles);
  };

  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  // Handle drag-and-drop for claim files
  const handleClaimDragOver = (e) => {
    e.preventDefault();
    setIsDraggingClaim(true);
  };

  const handleClaimDragLeave = () => {
    setIsDraggingClaim(false);
  };

  const handleClaimDrop = (e) => {
    e.preventDefault();
    setIsDraggingClaim(false);
    const droppedFiles = e.dataTransfer.files;
    handleClaimFileChange(droppedFiles);
  };

  const handleChooseClaimFile = () => {
    claimFileInputRef.current.click();
  };

  // Search Claims Logic

  const fetchClaims = async () => {
    if (!formData.provider_code) {
      return;
    }
    try {
      setIsTableLoading(true);
      const claimStatusFilter =
        filters.claim_status === "NULL" ? "" : filters.claim_status;
      console.log("Filter Values:", {
        ...filters,
        claim_status: claimStatusFilter,
      }); // Debugging
      const response = await axios.post(
        `${BASE_URL}/claims/search`,
        {
          provider_code: formData.provider_code,
          date_from: dateFilters.date_from || undefined,
          date_to:
            dateFilters.date_to || new Date().toISOString().split("T")[0],
          search_term: searchTerm,
          page: currentPage,
          per_page: itemsPerPage,
          sort_by:
            sortConfig.key === "date"
              ? "trans_date"
              : sortConfig.key === "transaction_no"
              ? "trans_no"
              : "member_name",
          sort_direction: sortConfig.direction,
          filters: {
            loa_no: filters.loa_no,
            transaction_no: filters.transaction_no,
            patient_name: filters.patient_name,
            coverage: filters.coverage,
            claim_status: claimStatusFilter, // Use the mapped value
          },
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setClaims(response.data.data);
      setTotalRecords(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
      setShowingEntries(response.data.pagination.showing_entries);
      console.log("Raw SQL Query:", response.data.raw_sql_query);
      console.log("Raw SQL Bindings:", response.data.raw_sql_bindings);
    } catch (error) {
      toast.error(
        "Failed to fetch claims: " +
          (error.response?.data?.message || error.message),
        { duration: 4000 }
      );
    } finally {
      setIsTableLoading(false);
    }
  };

  // Fetch claims when provider_code or received_date changes
  useEffect(() => {
    fetchClaims();
  }, [formData.provider_code, formData.received_date]);

  // Fetch claims when search, pagination, sort, or filters change (only if batch is saved)
  useEffect(() => {
    if (isBatchSaved) {
      fetchClaims();
    }
  }, [
    searchTerm,
    currentPage,
    itemsPerPage,
    sortConfig,
    filters,
    dateFilters,
    isBatchSaved,
  ]);

  const handleSearch = (e) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before searching claims.", {
        duration: 4000,
      });
      return;
    }
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before applying filters.", {
        duration: 4000,
      });
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before sorting.", {
        duration: 4000,
      });
      return;
    }
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const handlePageChange = (page) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before changing pages.", {
        duration: 4000,
      });
      return;
    }
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    if (!isBatchSaved) {
      toast.error(
        "Please save the batch header before changing items per page.",
        { duration: 4000 }
      );
      return;
    }
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2)
      startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-indigo-100"
          } ${!isBatchSaved ? "cursor-not-allowed opacity-50" : ""}`}
          disabled={!isBatchSaved}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  // Updated handleViewClaim
  const handleViewClaim = (claim) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before viewing claims.", {
        duration: 4000,
      });
      return;
    }
    setSelectedClaim(claim);
    setIsClaimDetailsModalOpen(true);
  };

  // Check if Claim is Already Batched
  const checkIsAlreadyBatched = async (transaction_no) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/claims/is-already-batched/${transaction_no}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      // return response.data.is_batched;
      return response.data;
    } catch (error) {
      toast.error(
        "Failed to check if claim is batched: " +
          (error.response?.data?.message || error.message),
        { duration: 4000 }
      );
      return false;
    }
  };

  const handleAttachClaim = async (claim) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before attaching claims.", {
        duration: 4000,
      });
      return;
    }

    if (claim.claim_status && claim.claim_status !== "NULL") {
      toast.error(`Only claims with NULL status can be attached.`, {
        duration: 4000,
      });
      return;
    }

    setIsProcessing(true);
    setIsProcessingText("Please wait while attaching...");

    // Check if claim is already batched
    // NOTES this is subject for removal if the request encouter slow
    const isAlreadyBatched = await checkIsAlreadyBatched(claim.transaction_no);
    if (isAlreadyBatched.is_batched) {
      toast.error(
        `Claim ${claim.loa_no} is already batched by: ${isAlreadyBatched.user[0].first_name} ${isAlreadyBatched.user[0].last_name}, on batch no: ${isAlreadyBatched.batch_no}`,
        {
          duration: 4000,
        }
      );
      setIsProcessing(false);
      setIsProcessingText("Please wait...");
      return;
    }

    if (claim.is_batched) {
      toast.error(`Claim ${claim.loa_no} is already batched.`, {
        duration: 4000,
      });
      setIsProcessing(false);
      setIsProcessingText("Please wait...");
      return;
    }

    if (!attachedClaims.some((c) => c.id === claim.id)) {
      try {
        const response = await axios.post(
          `${BASE_URL}/claims/attach-claim`,
          {
            batch_no: formData.batch_no,
            transaction_no: claim.transaction_no,
            loa_no: claim.loa_no,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );

        setAttachedClaims((prev) => [
          ...prev,
          { ...claim, attachedFiles: [], amount: Number(claim.amount) || 0 },
        ]);
        toast.success(`Claim ${claim.loa_no} attached successfully.`, {
          duration: 3000,
        });
        setIsProcessing(false);
        setIsProcessingText("Please wait...");

        // Refresh claims to update is_batched status
        fetchClaims();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to attach claim.",
          { duration: 4000 }
        );

        setIsProcessing(false);
        setIsProcessingText("Please wait...");
      }
    } else {
      toast.error(`Claim ${claim.loa_no} is already attached to this batch.`, {
        duration: 3000,
      });

      setIsProcessing(false);
      setIsProcessingText("Please wait...");
    }
  };

  const handleOpenAttachFilesModal = (claim) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before attaching files.", {
        duration: 4000,
      });
      return;
    }
    setSelectedClaimForFiles(claim);
    setClaimFiles(claim.attachedFiles || []);
    setIsAttachFilesModalOpen(true);
  };

  // const handleRemoveClaim = async (claim) => {
  //   if (!isBatchSaved) {
  //     toast.error("Please save the batch header before removing claims.", {
  //       duration: 4000,
  //     });
  //     return;
  //   }

  //   const hasAttachedFiles =
  //     claim.attachedFiles && claim.attachedFiles.length > 0;
  //   const result = await Swal.fire({
  //     title: "Remove Claim?",
  //     text: `Are you sure you want to remove attached claim ${claim.loa_no}? ${
  //       hasAttachedFiles
  //         ? "All uploaded files associated with this claim will also be deleted."
  //         : ""
  //     }`,
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#4f46e5",
  //     cancelButtonColor: "#ef4444",
  //     confirmButtonText: "Yes, remove it",
  //     cancelButtonText: "Cancel",
  //   });

  //   if (result.isConfirmed) {
  //     setIsProcessing(true);
  //     setIsProcessingText("Deleting attached files...");
  //     try {
  //       // Delete all attached files if they exist
  //       if (hasAttachedFiles) {
  //         const deletePromises = claim.attachedFiles
  //           .filter((fileObj) => fileObj.uploadId)
  //           .map((fileObj) =>
  //             axios.delete(
  //               `${BASE_URL}/claims/attach-files/${fileObj.uploadId}`,
  //               {
  //                 headers: {
  //                   Authorization: `Bearer ${localStorage.getItem(
  //                     "authentication/token"
  //                   )}`,
  //                 },
  //               }
  //             )
  //           );
  //         await Promise.all(deletePromises);
  //       }

  //       // Remove the claim from the state
  //       setAttachedClaims((prev) => prev.filter((c) => c.id !== claim.id));
  //       toast.success(`Claim ${claim.loa_no} removed successfully.`, {
  //         duration: 3000,
  //       });
  //       setIsProcessing(false);
  //       setIsProcessingText("Saving Batch...");
  //     } catch (error) {
  //       toast.error(
  //         error.response?.data?.message ||
  //           "Failed to remove claim or its files.",
  //         { duration: 4000 }
  //       );
  //       setIsProcessing(false);
  //       setIsProcessingText("Saving Batch...");
  //     }
  //   }
  // };

  // Handle save batch
  const handleRemoveClaim = async (claim) => {
    if (!isBatchSaved) {
      toast.error("Please save the batch header before removing claims.", {
        duration: 4000,
      });
      return;
    }

    const hasAttachedFiles =
      claim.attachedFiles && claim.attachedFiles.length > 0;
    const result = await Swal.fire({
      title: "Remove Claim?",
      text: `Are you sure you want to remove attached claim ${claim.loa_no}? ${
        hasAttachedFiles
          ? "All uploaded files associated with this claim will also be deleted."
          : ""
      }`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove it",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setIsProcessing(true);
      setIsProcessingText("Deleting attached files...");
      try {
        // Delete attached files if they exist
        if (hasAttachedFiles) {
          const deletePromises = claim.attachedFiles
            .filter((fileObj) => fileObj.uploadId)
            .map((fileObj) =>
              axios.delete(
                `${BASE_URL}/claims/attach-files/${fileObj.uploadId}`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "authentication/token"
                    )}`,
                  },
                }
              )
            );
          await Promise.all(deletePromises);
        }

        // Delete from v2_claims_is_already_batched
        await axios.delete(
          `${BASE_URL}/claims/attach-claim/${claim.transaction_no}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
            data: {
              batch_no: formData.batch_no,
            },
          }
        );

        // Remove the claim from the state
        setAttachedClaims((prev) => prev.filter((c) => c.id !== claim.id));
        toast.success(`Claim ${claim.loa_no} removed successfully.`, {
          duration: 3000,
        });

        // Refresh claims to update is_batched status
        fetchClaims();
        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "Failed to remove claim or its files.",
          { duration: 4000 }
        );
        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
      }
    }
  };

  const handleSaveBatch = async () => {
    const requiredFields = [
      { key: "received_date", label: "Date Received" },
      { key: "provider_code", label: "Provider Code" },
      { key: "provider_reference_no", label: "Hospital Reference No" },
      { key: "soa_amount", label: "SOA Amount" },
      { key: "accepted_by", label: "Accepted By" },
    ];
    for (const field of requiredFields) {
      const value = formData[field.key];
      if (value === undefined || value === null || String(value).trim() === "") {
        toast.error(`${field.label} is required.`, { duration: 4000 });
        return;
      }
    }

    setIsProcessing(true);
    try {
      const payload = {
        provider_code: formData.provider_code,
        received_date: formData.received_date,
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: Number(formData.soa_amount) || 0,
        batch_type: formData.batch_type,
        batch_status: "Draft",
        act_claim_count: formData.actual_claim_count,
        act_claim_amount: formData.actual_claim_amount,
        created_by: formData.created_by,
        accepted_by: formData.accepted_by,
        physician_code: formData.physician_code,
        remarks: formData.remarks,
        soa_fileupload: formData.soa_fileupload,
        batch_no: formData.batch_no,
        type: "save_header",
      };
      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/save`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setIsBatchSaved(true);
      Swal.fire({
        title: "Success!",
        text: "Batch header saved successfully.",
        icon: "success",
        confirmButtonColor: "#4f46e5",
      });
      setFormData((prev) => ({
        ...prev,
        est_claim_count: response.data.est_claim_count,
        est_claim_amount: response.data.est_claim_amount,
      }));
      hasUnsavedChanges.current = false;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save batch.", {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle submit batch
  const handleSubmit = async () => {
    if (!isBatchSaved) {
      // Save batch header first
      await handleSaveBatch();
      if (!isBatchSaved) {
        toast.error("Failed to save batch header. Please try again.", {
          duration: 4000,
        });
        return;
      }
    }

    if (attachedClaims.length === 0) {
      toast.error("At least one claim must be attached.", { duration: 4000 });
      return;
    }

    setIsConfirmModalOpen(true); // Show confirmation modal
  };

  // Handle save batch ForSavingOnly
  const handleSaveBatch_ForSavingOnly = async () => {
    const requiredFields = [
      { key: "received_date", label: "Date Received" },
      { key: "provider_code", label: "Provider Code" },
      { key: "provider_reference_no", label: "Hospital Reference No" },
      { key: "soa_amount", label: "SOA Amount" },
      { key: "accepted_by", label: "Accepted By" },
    ];
    for (const field of requiredFields) {
      const value = formData[field.key];
      if (value === undefined || value === null || String(value).trim() === "") {
        toast.error(`${field.label} is required.`, { duration: 4000 });
        return;
      }
    }

    setIsProcessing(true);
    try {
      const payload = {
        provider_code: formData.provider_code,
        received_date: formData.received_date,
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: Number(formData.soa_amount) || 0,
        batch_type: formData.batch_type,
        batch_status: "Open",
        act_claim_count: formData.actual_claim_count,
        act_claim_amount: formData.actual_claim_amount,
        created_by: formData.created_by,
        accepted_by: formData.accepted_by,
        physician_code: formData.physician_code,
        remarks: formData.remarks,
        soa_fileupload: formData.soa_fileupload,
        batch_no: formData.batch_no,
        type: "save_header",
      };
      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/submit-save-only`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setIsBatchSaved(true);
      Swal.fire({
        title: "Success!",
        text: "Batch header saved successfully.",
        icon: "success",
        confirmButtonColor: "#4f46e5",
      });
      setFormData((prev) => ({
        ...prev,
        est_claim_count: response.data.est_claim_count,
        est_claim_amount: response.data.est_claim_amount,
      }));
      hasUnsavedChanges.current = false;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save batch.", {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle submit batch ForSavingOnly
  const handleSubmit_ForSavingOnly = async () => {
    if (!isBatchSaved) {
      // Save batch header first
      await handleSaveBatch_ForSavingOnly();
      if (!isBatchSaved) {
        toast.error("Failed to save batch header. Please try again.", {
          duration: 4000,
        });
        return;
      }
    }

    // if (attachedClaims.length === 0) {
    //   toast.error("At least one claim must be attached.", { duration: 4000 });
    //   return;
    // }

    // setIsConfirmModalOpen(true); // Show confirmation modal

    handleConfirmSubmit_ForSavingOnly();
  };

  // Handle confirm submit ForSavingOnly
  const handleConfirmSubmit_ForSavingOnly = async () => {
    setIsProcessing(true);
    setIsTableLoading(true);
    try {
      const payload = {
        is_save_only: true,
        batch_no: formData.batch_no,
        provider_code: formData.provider_code,
        received_date: formData.received_date,
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: Number(formData.soa_amount) || 0,
        batch_type: formData.batch_type,
        est_claim_count: formData.est_claim_count,
        est_claim_amount: formData.est_claim_amount,
        act_claim_count: formData.actual_claim_count,
        act_claim_amount: formData.actual_claim_amount,
        created_by: formData.created_by,
        accepted_by: formData.accepted_by,
        physician_code: formData.physician_code,
        remarks: formData.remarks,
        soa_fileupload: formData.soa_fileupload,
        claims: attachedClaims.map((claim) => ({
          transaction_no: claim.transaction_no,
          claim_status: claim.claim_status || null,
          claim_remarks: claim.claim_remarks || "",
        })),
      };

      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/submit-save-only`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      // Show success and prompt for next action
      const result = await Swal.fire({
        title: "Batch Saved Successfully!",
        text: "What would you like to do next?",
        icon: "success",
        // showCancelButton: true,
        confirmButtonColor: "#4f46e5",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Go to Claims Batch List",
        cancelButtonText: "Go to Claims Batch List",
        customClass: {
          popup: "rounded-xl shadow-lg",
          title: "text-lg font-semibold text-gray-900",
          content: "text-sm text-gray-600",
          confirmButton:
            "px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-all",
          cancelButton:
            "px-4 py-2 rounded-md text-white bg-gray-500 hover:bg-gray-600 transition-all",
        },
      });

      if (result.isConfirmed) {
        navigate("/claims/batches");
        // Create New Batch
        // setFormData({
        //   batch_no: "",
        //   batch_status: "Open",
        //   received_date: "",
        //   due_date: "",
        //   provider_code: "",
        //   provider_reference_no: "",
        //   accepted_by: "",
        //   actual_claim_count: 0,
        //   actual_claim_amount: 0,
        //   batch_type: "Claims",
        //   est_claim_count: 0,
        //   est_claim_amount: 0,
        //   created_by: "system_user",
        //   physician_code: null,
        //   remarks: "",
        //   soa_fileupload: "[]",
        // });
        // setFiles([]);
        // setAttachedClaims([]);
        // setIsBatchSaved(false);
        // setIsConfirmModalOpen(false);
        // generateBatchNumber();
      } else {
        // Navigate to Claims Batch List
        navigate("/claims/batches");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit batch.", {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
      setIsTableLoading(false);
    }
  };

  // Handle confirm submit
  const handleConfirmSubmit = async () => {
    setIsProcessing(true);
    setIsTableLoading(true);
    try {
      const payload = {
        is_save_only: false,
        batch_no: formData.batch_no,
        provider_code: formData.provider_code,
        received_date: formData.received_date,
        due_date: formData.due_date,
        provider_reference_no: formData.provider_reference_no,
        soa_amount: Number(formData.soa_amount) || 0,
        batch_type: formData.batch_type,
        est_claim_count: formData.est_claim_count,
        est_claim_amount: formData.est_claim_amount,
        act_claim_count: formData.actual_claim_count,
        act_claim_amount: formData.actual_claim_amount,
        created_by: formData.created_by,
        accepted_by: formData.accepted_by,
        physician_code: formData.physician_code,
        remarks: formData.remarks,
        soa_fileupload: formData.soa_fileupload,
        claims: attachedClaims.map((claim) => ({
          transaction_no: claim.transaction_no,
          claim_status: claim.claim_status || null,
          claim_remarks: claim.claim_remarks || "",
        })),
      };

      const response = await axios.post(
        `${BASE_URL}/claims/batch-header/submit`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      // Show success and prompt for next action
      const result = await Swal.fire({
        title: "Batch Submitted Successfully!",
        text: "What would you like to do next?",
        icon: "success",
        showCancelButton: true,
        confirmButtonColor: "#4f46e5",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Create New Batch",
        cancelButtonText: "Go to Claims Batch List",
        customClass: {
          popup: "rounded-xl shadow-lg",
          title: "text-lg font-semibold text-gray-900",
          content: "text-sm text-gray-600",
          confirmButton:
            "px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-all",
          cancelButton:
            "px-4 py-2 rounded-md text-white bg-gray-500 hover:bg-gray-600 transition-all",
        },
      });

      if (result.isConfirmed) {
        // Create New Batch
        setFormData({
          batch_no: "",
          batch_status: "Open",
          received_date: "",
          due_date: "",
          provider_code: "",
          provider_reference_no: "",
          soa_amount: "",
          accepted_by: "",
          actual_claim_count: 0,
          actual_claim_amount: 0,
          batch_type: "Claims",
          est_claim_count: 0,
          est_claim_amount: 0,
          created_by: "system_user",
          physician_code: null,
          remarks: "",
          soa_fileupload: "[]",
        });
        setFiles([]);
        setAttachedClaims([]);
        setIsBatchSaved(false);
        setIsConfirmModalOpen(false);
        generateBatchNumber();
      } else {
        // Navigate to Claims Batch List
        navigate("/claims/batches");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit batch.", {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
      setIsTableLoading(false);
    }
  };

  // New function to handle scanning
  // const handleScanFiles = (transactionNo) => {
  //   setScanning(true);
  //   window.scanner.scan(
  //     (success, response) => {
  //       if (!success) {
  //         setScanning(false);
  //         toast.error('Scanning failed: ' + (response.message || 'Unknown error'));
  //         if (response.message.includes('Asprise Scan Client')) {
  //           toast.error(
  //             'Please install the Asprise Scan Client: http://asprise.com/scan/client/download.php',
  //             { duration: 6000 }
  //           );
  //         }
  //         return;
  //       }

  //       const scannedImages = window.scanner.getScannedImages(response, true, false); // Get images
  //       if (scannedImages.length === 0) {
  //         setScanning(false);
  //         toast.error('No images scanned.');
  //         return;
  //       }

  //       // Convert scanned images to File objects for upload
  //       const files = scannedImages.map((img, index) => {
  //         const blob = dataURLtoBlob(img.src); // Convert data URL to Blob
  //         return new File([blob], `scanned_${transactionNo}_${index + 1}.${img.src.includes('jpeg') ? 'jpg' : 'png'}`, {
  //           type: img.src.includes('jpeg') ? 'image/jpeg' : 'image/png',
  //         });
  //       });

  //       // Call existing handleAttachFiles to upload scanned files
  //       handleAttachFiles(transactionNo, files);
  //       setScanning(false);
  //     },
  //     {
  //       source: 'scanner', // Use TWAIN/WIA scanner
  //       format: 'jpg', // Can also use 'png' or 'pdf'
  //       output_settings: [
  //         {
  //           type: 'return-base64', // Return images as base64 for processing
  //           format: 'jpg',
  //         },
  //       ],
  //     }
  //   );
  // };

  // const handleScanFiles = (claim) => {
  //   setIsProcessing(true);
  //   setIsProcessingText("Scanning documents...");

  //   window.scanner.scan(
  //     (success, response) => {
  //       if (!success || !response || !response.output) {
  //         setIsProcessing(false);
  //         setIsProcessingText("Saving Batch...");
  //         if (!success) {
  //           toast.error(
  //             "Scanning failed: " + (response?.message || "Unknown error"),
  //             { duration: 4000 }
  //           );
  //           if (response?.message?.includes("Asprise Scan Client")) {
  //             toast.error(
  //               "Please install the Asprise Scan Client: http://asprise.com/scan/client/download.php",
  //               { duration: 6000 }
  //             );
  //           }
  //         } else {
  //           toast.error("No images scanned or invalid response from scanner.", {
  //             duration: 4000,
  //           });
  //         }
  //         return;
  //       }

  //       const scannedImages = window.scanner.getScannedImages(
  //         response,
  //         true,
  //         false
  //       );
  //       if (scannedImages.length === 0) {
  //         setIsProcessing(false);
  //         setIsProcessingText("Saving Batch...");
  //         toast.error("No images were scanned.", { duration: 4000 });
  //         return;
  //       }

  //       // Convert scanned images to File objects
  //       const newFiles = scannedImages.map((img, index) => {
  //         const blob = dataURLtoBlob(img.src);
  //         return {
  //           file: new File(
  //             [blob],
  //             `scanned_${claim.transaction_no}_${index + 1}.${
  //               img.src.includes("jpeg") ? "jpg" : "png"
  //             }`,
  //             {
  //               type: img.src.includes("jpeg") ? "image/jpeg" : "image/png",
  //             }
  //           ),
  //           progress: 0,
  //           isUploading: false,
  //           uploadId: null,
  //           signed_url: null,
  //           error: null,
  //         };
  //       });

  //       // Update claimFiles state and trigger uploads
  //       setClaimFiles((prevFiles) => [...prevFiles, ...newFiles]);
  //       newFiles.forEach((fileObj) => handleClaimFileUpload(fileObj));

  //       setIsProcessing(false);
  //       setIsProcessingText("Saving Batch...");
  //     },
  //     {
  //       source: "scanner",
  //       format: "jpg",
  //       output_settings: [
  //         {
  //           type: "return-base64",
  //           format: "jpg",
  //         },
  //       ],
  //     }
  //   );
  // };

  const handleScanFiles = (claim) => {
    setIsProcessing(true);
    setIsProcessingText("Scanning documents...");

    window.scanner.scan(
      (success, response) => {
        console.log("Scanner.js response:", { success, response }); // Log response for debugging
        if (!success || !response || !response.output) {
          setIsProcessing(false);
          setIsProcessingText("Saving Batch...");
          if (!success) {
            toast.error(
              "Scanning failed: " + (response?.message || "Unknown error"),
              { duration: 4000 }
            );
            if (response?.message?.includes("Asprise Scan Client")) {
              toast.error(
                "Please install the Asprise Scan Client: http://asprise.com/scan/client/download.php",
                { duration: 6000 }
              );
            }
          } else {
            toast.error("No images scanned or invalid response from scanner.", {
              duration: 4000,
            });
          }
          return;
        }

        const scannedImages = window.scanner.getScannedImages(
          response,
          true,
          false
        );
        console.log("Scanned images:", scannedImages); // Log images for debugging
        if (scannedImages.length === 0) {
          setIsProcessing(false);
          setIsProcessingText("Saving Batch...");
          toast.error("No images were scanned.", { duration: 4000 });
          return;
        }

        const newFiles = scannedImages.map((img, index) => {
          const blob = dataURLtoBlob(img.src);
          return {
            file: new File(
              [blob],
              `scanned_${claim.transaction_no}_${index + 1}.${
                img.src.includes("jpeg") ? "jpg" : "png"
              }`,
              {
                type: img.src.includes("jpeg") ? "image/jpeg" : "image/png",
              }
            ),
            progress: 0,
            isUploading: false,
            uploadId: null,
            signed_url: null,
            error: null,
          };
        });

        setClaimFiles((prevFiles) => [...prevFiles, ...newFiles]);
        newFiles.forEach((fileObj) => handleClaimFileUpload(fileObj));

        setIsProcessing(false);
        setIsProcessingText("Saving Batch...");
      },
      {
        source: "scanner",
        format: "jpg",
        output_settings: [
          {
            type: "return-base64",
            format: "jpg",
          },
        ],
      }
    );
  };

  // Utility to convert data URL to Blob (for scanned images)
  // const dataURLtoBlob = (dataURL) => {
  //   const arr = dataURL.split(',');
  //   const mime = arr[0].match(/:(.*?);/)[1];
  //   const bstr = atob(arr[1]);
  //   let n = bstr.length;
  //   const u8arr = new Uint8Array(n);
  //   while (n--) {
  //     u8arr[n] = bstr.charCodeAt(n);
  //   }
  //   return new Blob([u8arr], { type: mime });
  // };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <Tooltip id="provider-tooltip" />
      <Tooltip id="search-claims-tooltip" />
      <Tooltip id="filter-claims-tooltip" />
      <Tooltip id="view-claim-tooltip" />
      <Tooltip id="attach-claim-tooltip" />
      <Tooltip id="attach-files-tooltip" />
      <Tooltip id="return-provider-tooltip" />
      <Tooltip id="remove-claim-tooltip" />
      <Tooltip id="view-file-tooltip" />
      <Tooltip id="remove-file-tooltip" />
      <Tooltip id="save-batch-tooltip" />
      <Tooltip id="submit-batch-tooltip" />
      <Tooltip id="items-per-page-tooltip" />

      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white rounded-2xl shadow-lg">
            <FontAwesomeIcon
              icon={faUpload}
              className="text-indigo-600 text-lg"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Add Claims Batch & Received/Scanned Date Uploading
            </h1>
            <p className="text-sm text-gray-500">
              Create a new claims batch and attach claims
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Batch Registration Set-up (Left) */}
          {isStep1Visible && (
            <div className="md:w-1/3 bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                  Batch Registration Set-up
                </h2>
                <button
                  onClick={() => setIsStep1Visible(false)}
                  className="text-indigo-600 hover:text-indigo-800"
                  aria-label="Hide Batch Registration Set-up"
                >
                  <FontAwesomeIcon icon={faEyeSlash} />
                </button>
              </div>
              <div className="p-6">
                {/* Form Fields */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Batch Number
                    </label>
                    <input
                      type="text"
                      value={formData.batch_no}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Batch Status
                    </label>
                    <input
                      type="text"
                      value={formData.batch_status}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Date Received *
                    </label>
                    <input
                      type="datetime-local"
                      name="received_date"
                      value={formData.received_date}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={formData.due_date}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Provider *
                    </label>
                    <input
                      type="text"
                      value={selectedProviderName}
                      onChange={handleProviderSearch}
                      placeholder="Search providers..."
                      className={`mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        !formData.received_date
                          ? "bg-gray-100 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={!formData.received_date}
                      data-tooltip-id="provider-tooltip"
                      data-tooltip-content={
                        !formData.received_date
                          ? "Enter Date Received to search providers"
                          : "Search providers"
                      }
                    />
                    {providers.length > 0 && formData.received_date && (
                      <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                        {providers.map((provider) => (
                          <li
                            key={provider.id}
                            onClick={() => handleProviderSelect(provider)}
                            className="px-3 py-2 text-sm hover:bg-indigo-100 cursor-pointer"
                          >
                            {provider.provider_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Hospital Reference No *
                    </label>
                    <input
                      type="text"
                      name="provider_reference_no"
                      value={formData.provider_reference_no}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      SOA Amount *
                    </label>
                    <input
                      type="number"
                      name="soa_amount"
                      min="0"
                      step="0.01"
                      value={formData.soa_amount}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Accepted By *
                    </label>
                    <input
                      type="text"
                      name="accepted_by"
                      value={formData.accepted_by}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Actual Claim Count
                    </label>
                    <input
                      type="number"
                      value={formData.actual_claim_count}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Actual Claim Amount
                    </label>
                    <input
                      type="text"
                      value={phpFormatter.format(formData.actual_claim_amount)}
                      disabled
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Remarks
                    </label>
                    <textarea
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      rows="4"
                      placeholder="Enter any additional remarks..."
                    />
                  </div>
                </div>

                {/* File Upload Area */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Attach Statement of Account (SOA)
                  </h3>
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                      isDragging
                        ? "border-indigo-500 bg-indigo-50 scale-105"
                        : "border-gray-300 bg-gray-50"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <FontAwesomeIcon
                      icon={faUpload}
                      className="text-4xl text-indigo-500 mb-4"
                    />
                    <p className="text-gray-700 text-lg">
                      Drag and drop PDF or image files here or{" "}
                      <button
                        type="button"
                        onClick={handleChooseFile}
                        className="text-indigo-600 font-semibold hover:text-indigo-800 transition-colors"
                      >
                        choose files
                      </button>
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Supported formats: .pdf, .jpg, .jpeg, .png (Max{" "}
                      {maxFileSizeMB}MB per file, up to {maxUploadLimit} files)
                    </p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e.target.files)}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Selected (SOA) Files
                    </h3>
                    <ul className="space-y-4">
                      {files.map((fileObj, index) => (
                        <li
                          key={index}
                          className="flex items-center justify-between bg-gray-100 p-4 rounded-lg shadow-sm"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {fileObj.file.name}
                            </p>
                            {fileObj.isUploading && (
                              <div className="relative pt-2">
                                <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${fileObj.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 mt-1 block">
                                  {fileObj.progress}% Uploading
                                </span>
                              </div>
                            )}
                            {fileObj.error && (
                              <p className="text-xs text-red-600 mt-1">
                                Error: {fileObj.error}
                              </p>
                            )}
                            {fileObj.uploadId && (
                              <p className="text-xs text-green-600 mt-1">
                                Uploaded (ID: {fileObj.uploadId})
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {fileObj.signed_url && (
                              <button
                                onClick={() => handleViewFile(fileObj)}
                                className="text-indigo-600 hover:text-indigo-800"
                                data-tooltip-id="view-file-tooltip"
                                data-tooltip-content="View file"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                            )}
                            <button
                              onClick={() => handleRemoveFile(fileObj)}
                              className="text-red-500 hover:text-red-700"
                              disabled={fileObj.isUploading}
                              data-tooltip-id="remove-file-tooltip"
                              data-tooltip-content="Remove file"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSaveBatch}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                    disabled={
                      isProcessing ||
                      files.some((f) => f.isUploading) ||
                      !formData.batch_no
                    }
                    data-tooltip-id="save-batch-tooltip"
                    data-tooltip-content="Save batch header"
                  >
                    <FontAwesomeIcon icon={faSave} />
                    Save Batch Header
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Search Claims (Right) */}
          <div
            className={`bg-white rounded-2xl shadow-xl overflow-hidden ${
              isStep1Visible ? "md:w-2/3" : "w-full"
            }`}
          >
            <div className="p-6 flex justify-between items-center border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800">
                Search Claims
              </h2>
              {!isStep1Visible && (
                <button
                  onClick={() => setIsStep1Visible(true)}
                  className="text-indigo-600 hover:text-indigo-800"
                  aria-label="Show Batch Registration Set-up"
                >
                  Show Batch Registration Set-up{" "}
                  <FontAwesomeIcon icon={faEye} />
                </button>
              )}
            </div>
            <div className="p-6">
              {/* Search Results Table */}

              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Search Results
                </h3>
                <div className="flex flex-col gap-4 mb-4">
                  {/* Search Input */}
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Search claims..."
                      value={searchTerm}
                      onChange={handleSearch}
                      className={`w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        !isBatchSaved ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      disabled={!isBatchSaved}
                      data-tooltip-id="search-claims-tooltip"
                      data-tooltip-content={
                        !isBatchSaved
                          ? "Save batch header to search claims"
                          : "Search claims"
                      }
                    />
                  </div>
                  {/* Date Filters */}
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Date From
                      </label>
                      <input
                        type="date"
                        value={dateFilters.date_from}
                        onChange={(e) =>
                          setDateFilters((prev) => ({
                            ...prev,
                            date_from: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          !isBatchSaved ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                        disabled={!isBatchSaved}
                        data-tooltip-id="filter-claims-tooltip"
                        data-tooltip-content={
                          !isBatchSaved
                            ? "Save batch header to filter claims"
                            : "Filter by Date From"
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Date To
                      </label>
                      <input
                        type="date"
                        value={dateFilters.date_to}
                        onChange={(e) =>
                          setDateFilters((prev) => ({
                            ...prev,
                            date_to: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          !isBatchSaved ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                        disabled={!isBatchSaved}
                        data-tooltip-id="filter-claims-tooltip"
                        data-tooltip-content={
                          !isBatchSaved
                            ? "Save batch header to filter claims"
                            : "Filter by Date To"
                        }
                      />
                    </div>
                  </div>

                  {/* Existing Filters */}
                  <div className="flex gap-4 overflow-x-auto">
                    {columns.map(
                      (col) =>
                        col.key !== "actions" &&
                        col.key !== "amount" &&
                        col.key !== "date" &&
                        col.key !== "stale" &&
                        col.key !== "claim_status" && ( // Exclude claim_status here
                          <div key={col.key} className="flex-1 min-w-[150px]">
                            <input
                              type="text"
                              placeholder={`Filter by ${col.label}`}
                              value={filters[col.key]}
                              onChange={(e) =>
                                handleFilterChange(col.key, e.target.value)
                              }
                              className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                !isBatchSaved
                                  ? "bg-gray-100 cursor-not-allowed"
                                  : ""
                              }`}
                              disabled={!isBatchSaved}
                              data-tooltip-id="filter-claims-tooltip"
                              data-tooltip-content={
                                !isBatchSaved
                                  ? "Save batch header to filter claims"
                                  : `Filter by ${col.label}`
                              }
                            />
                          </div>
                        )
                    )}
                    {/* Add Claim Status Dropdown */}
                    <div className="flex-1 min-w-[150px]">
                      {/* <label className="block text-sm font-medium text-gray-700">Claim Status</label> */}
                      <select
                        value={filters.claim_status}
                        onChange={(e) =>
                          handleFilterChange("claim_status", e.target.value)
                        }
                        className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          !isBatchSaved ? "bg-gray-100 cursor-not-allowed" : ""
                        }`}
                        disabled={!isBatchSaved}
                        data-tooltip-id="filter-claims-tooltip"
                        data-tooltip-content={
                          !isBatchSaved
                            ? "Save batch header to filter claims"
                            : "Filter by Claim Status"
                        }
                      >
                        <option value="">All</option>
                        <option value="NULL">NULL</option>
                        {Object.keys(claimStatusStyles)
                          .filter((status) => status !== "NULL")
                          .map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto relative">
                  {isTableLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="text-indigo-600 text-2xl animate-spin"
                      />
                    </div>
                  )}
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        {columns.map((column) => (
                          <th
                            key={column.key}
                            className={`p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                              column.sortable && isBatchSaved
                                ? "cursor-pointer hover:bg-gray-100"
                                : ""
                            }`}
                            onClick={() =>
                              column.sortable && handleSort(column.key)
                            }
                          >
                            <div className="flex items-center gap-1">
                              {column.label}
                              {column.sortable && (
                                <div className="flex flex-col text-xs">
                                  <span
                                    className={`${
                                      sortConfig.key === column.key &&
                                      sortConfig.direction === "asc"
                                        ? "text-indigo-600"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    ▲
                                  </span>
                                  <span
                                    className={`${
                                      sortConfig.key === column.key &&
                                      sortConfig.direction === "desc"
                                        ? "text-indigo-600"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    ▼
                                  </span>
                                </div>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {claims.length === 0 && !isTableLoading ? (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="p-3 text-center text-gray-500 text-sm"
                          >
                            No claims found.
                          </td>
                        </tr>
                      ) : (
                        claims.map((claim) => (
                          <tr
                            key={claim.id}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="p-3 text-sm">{claim.loa_no}</td>
                            <td className="p-3 text-sm">
                              {claim.transaction_no}
                            </td>
                            <td className="p-3 text-sm">
                              {phpFormatter.format(claim.amount)}
                            </td>
                            <td className="p-3 text-sm">
                              {claim.patient_name}
                            </td>
                            <td className="p-3 text-sm">{claim.coverage}</td>
                            <td className="p-3 text-sm">{claim.date}</td>
                            <td className="p-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  claimStatusStyles[claim.claim_status] ||
                                  claimStatusStyles["NULL"]
                                }`}
                              >
                                {claim.claim_status || "NULL"}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              {claim.stale ? "Yes" : "No"}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleViewClaim(claim)}
                                  className={`px-3 py-1 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1 ${
                                    !isBatchSaved
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={!isBatchSaved}
                                  data-tooltip-id="view-claim-tooltip"
                                  data-tooltip-content={
                                    !isBatchSaved
                                      ? "Save batch header to view claims"
                                      : "View claim details"
                                  }
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  View
                                </button>
                                <button
                                  onClick={() => handleAttachClaim(claim)}
                                  className={`px-3 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-1 ${
                                    !isBatchSaved ||
                                    (claim.claim_status &&
                                      claim.claim_status !== "NULL")
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={
                                    !isBatchSaved ||
                                    (claim.claim_status &&
                                      claim.claim_status !== "NULL")
                                  }
                                  data-tooltip-id="attach-claim-tooltip"
                                  data-tooltip-content={
                                    !isBatchSaved
                                      ? "Save batch header to attach claims"
                                      : claim.claim_status &&
                                        claim.claim_status !== "NULL"
                                      ? "Only NULL status claims can be attached"
                                      : "Attach claim to batch"
                                  }
                                >
                                  <FontAwesomeIcon icon={faPaperclip} />
                                  Attach Claim
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-col md:flex-row md:items-center justify-between p-6 border-t border-gray-100">
                  <div className="mb-3 md:mb-0">
                    <select
                      value={itemsPerPage}
                      onChange={(e) =>
                        handleItemsPerPageChange(Number(e.target.value))
                      }
                      className={`px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        !isBatchSaved ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      disabled={!isBatchSaved}
                      data-tooltip-id="items-per-page-tooltip"
                      data-tooltip-content={
                        !isBatchSaved
                          ? "Save batch header to change items per page"
                          : "Items per page"
                      }
                    >
                      {[5, 10, 20, 50, 100].map((val) => (
                        <option key={val} value={val}>
                          {val} per page
                        </option>
                      ))}
                    </select>
                    <span className="ml-2 text-sm text-gray-600">
                      {showingEntries}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || !isBatchSaved}
                      className={`p-2 text-gray-700 rounded-lg hover:bg-indigo-100 ${
                        currentPage === 1 || !isBatchSaved
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <FontAwesomeIcon icon={faAngleDoubleLeft} />
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || !isBatchSaved}
                      className={`p-2 text-gray-700 rounded-lg hover:bg-indigo-100 ${
                        currentPage === 1 || !isBatchSaved
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                    {renderPageNumbers()}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || !isBatchSaved}
                      className={`p-2 text-gray-700 rounded-lg hover:bg-indigo-100 ${
                        currentPage === totalPages || !isBatchSaved
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || !isBatchSaved}
                      className={`p-2 text-gray-700 rounded-lg hover:bg-indigo-100 ${
                        currentPage === totalPages || !isBatchSaved
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <FontAwesomeIcon icon={faAngleDoubleRight} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Added/Attached Claims Table */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Added/Attached Claims
                </h3>
                <div className="overflow-x-auto relative">
                  {isTableLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="text-indigo-600 text-2xl animate-spin"
                      />
                    </div>
                  )}
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        {columns.map((column) => (
                          <th
                            key={column.key}
                            className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                          >
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    {/* <tbody className="divide-y divide-gray-100">
                      {attachedClaims.length === 0 && !isTableLoading ? (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="p-3 text-center text-gray-500 text-sm"
                          >
                            No claims attached.
                          </td>
                        </tr>
                      ) : (
                        attachedClaims.map((claim) => (
                          <tr
                            key={claim.id}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="p-3 text-sm">{claim.loa_no}</td>
                            <td className="p-3 text-sm">
                              {claim.transaction_no}
                            </td>
                            <td className="p-3 text-sm">
                              {phpFormatter.format(claim.amount)}
                            </td>
                            <td className="p-3 text-sm">
                              {claim.patient_name}
                            </td>
                            <td className="p-3 text-sm">{claim.coverage}</td>
                            <td className="p-3 text-sm">{claim.date}</td>
                            <td className="p-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  claimStatusStyles[claim.claim_status] ||
                                  claimStatusStyles["NULL"]
                                }`}
                              >
                                {claim.claim_status || "NULL"}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              {claim.stale ? "Yes" : "No"}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleViewClaim(claim)}
                                  className={`px-3 py-1 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1 ${
                                    !isBatchSaved
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={!isBatchSaved}
                                  data-tooltip-id="view-claim-tooltip"
                                  data-tooltip-content={
                                    !isBatchSaved
                                      ? "Save batch header to view claims"
                                      : "View claim details"
                                  }
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  View
                                </button>

                                <button
                                  onClick={() =>
                                    handleOpenAttachFilesModal(claim)
                                  }
                                  className={`px-3 py-1 text-xs text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-1 ${
                                    !isBatchSaved
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={!isBatchSaved}
                                  data-tooltip-id="attach-files-tooltip"
                                  data-tooltip-content={
                                    !isBatchSaved
                                      ? "Save batch header to attach files"
                                      : "Attach files to claim"
                                  }
                                >
                                  <FontAwesomeIcon icon={faPaperclip} />
                                  Attach Files
                                </button>

                                <button
                                  onClick={() => handleRemoveClaim(claim)}
                                  className={`px-3 py-1 text-xs text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center gap-1 ${
                                    !isBatchSaved
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={!isBatchSaved}
                                  data-tooltip-id="remove-claim-tooltip"
                                  data-tooltip-content={
                                    !isBatchSaved
                                      ? "Save batch header to remove claims"
                                      : "Remove claim from batch"
                                  }
                                >
                                  <FontAwesomeIcon icon={faTimesCircle} />
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody> */}

                    <tbody className="divide-y divide-gray-100">
                      {attachedClaims.length === 0 && !isTableLoading ? (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="p-3 text-center text-gray-500 text-sm"
                          >
                            No claims attached.
                          </td>
                        </tr>
                      ) : (
                        attachedClaims.map((claim) => (
                          <tr
                            key={claim.id}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="p-3 text-sm">{claim.loa_no}</td>
                            <td className="p-3 text-sm">
                              {claim.transaction_no}
                            </td>
                            <td className="p-3 text-sm">
                              {phpFormatter.format(claim.amount)}
                            </td>
                            <td className="p-3 text-sm">
                              {claim.patient_name}
                            </td>
                            <td className="p-3 text-sm">{claim.coverage}</td>
                            <td className="p-3 text-sm">{claim.date}</td>
                            <td className="p-3 text-sm">
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  claimStatusStyles[claim.claim_status] ||
                                  claimStatusStyles["NULL"]
                                }`}
                              >
                                {claim.claim_status || "NULL"}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              {claim.stale ? "Yes" : "No"}
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleViewClaim(claim)}
                                  className="px-3 py-1 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1"
                                  data-tooltip-id="view-claim-tooltip"
                                  data-tooltip-content="View claim details"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  View
                                </button>
                                <button
                                  onClick={() =>
                                    handleOpenAttachFilesModal(claim)
                                  }
                                  className={`px-3 py-1 text-xs text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-1 ${
                                    !isBatchSaved
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={!isBatchSaved}
                                  data-tooltip-id="attach-files-tooltip"
                                  data-tooltip-content={
                                    !isBatchSaved
                                      ? "Save batch header to attach files"
                                      : "Attach files manually to claim"
                                  }
                                >
                                  <FontAwesomeIcon icon={faPaperclip} />
                                  Attach Files Manually
                                </button>
                                <button
                                  onClick={() => handleScanFiles(claim)}
                                  className={`px-3 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-1 ${
                                    !isBatchSaved || isProcessing
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={!isBatchSaved || isProcessing}
                                  data-tooltip-id="scan-files-tooltip"
                                  data-tooltip-content={
                                    !isBatchSaved
                                      ? "Save batch header to scan files"
                                      : "Scan files to attach to claim"
                                  }
                                >
                                  <FontAwesomeIcon
                                    icon={isProcessing ? faSpinner : faCamera}
                                    spin={isProcessing}
                                  />
                                  Scan Files to Attach
                                </button>
                                <button
                                  onClick={() => handleRemoveClaim(claim)}
                                  className={`px-3 py-1 text-xs text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center gap-1 ${
                                    !isBatchSaved
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  disabled={!isBatchSaved}
                                  data-tooltip-id="remove-claim-tooltip"
                                  data-tooltip-content={
                                    !isBatchSaved
                                      ? "Save batch header to remove claims"
                                      : "Remove claim from batch"
                                  }
                                >
                                  <FontAwesomeIcon icon={faTimesCircle} />
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-6">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Attached Claims TESTING
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Transaction No.
                        </th>
                        <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          LOA No.
                        </th>
                        <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Patient Name
                        </th>
                        <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Attached Files
                        </th>
                        <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {claims.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="p-3 text-center text-gray-500 text-sm"
                          >
                            No claims attached.
                          </td>
                        </tr>
                      ) : (
                        claims.map((claim) => (
                          <tr
                            key={claim.transaction_no}
                            data-transaction-no={claim.transaction_no}
                          >
                            <td className="p-3 text-sm">
                              {claim.transaction_no}
                            </td>
                            <td className="p-3 text-sm">
                              {claim.loa_no || "-"}
                            </td>
                            <td className="p-3 text-sm">
                              {claim.patient_name || "-"}
                            </td>
                            <td className="p-3 text-sm">
                              {claim.amount
                                ? new Intl.NumberFormat("en-PH", {
                                    style: "currency",
                                    currency: "PHP",
                                  }).format(claim.amount)
                                : "-"}
                            </td>
                            <td className="p-3 text-sm">
                              {(attachedFiles[claim.transaction_no] || []).map(
                                (file) => (
                                  <div
                                    key={file.upload_id}
                                    className="flex items-center gap-2"
                                  >
                                    <a
                                      href={file.signed_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-indigo-600 hover:underline"
                                    >
                                      {file.original_file_name}
                                    </a>
                                  </div>
                                )
                              )}
                            </td>
                            <td className="p-3 flex gap-2">
                              {/* Manual Attach Files */}
                              <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                <button
                                  className="flex items-center gap-1 px-3 py-1 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all duration-200"
                                  disabled={loading}
                                  title="Attach Files Manually"
                                >
                                  <FontAwesomeIcon icon={faUpload} />
                                  {loading
                                    ? "Uploading..."
                                    : "Attach Files Manually"}
                                </button>
                              </div>
                              {/* Scan Files to Attach */}
                              <button
                                onClick={() =>
                                  handleScanFiles(claim.transaction_no)
                                }
                                className="flex items-center gap-1 px-3 py-1 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all duration-200"
                                disabled={scanning}
                                title="Scan Files to Attach"
                              >
                                <FontAwesomeIcon
                                  icon={scanning ? faSpinner : faCamera}
                                  spin={scanning}
                                />
                                {scanning
                                  ? "Scanning..."
                                  : "Scan Files to Attach"}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Submit Button */}
              {/* <div className="flex justify-end mt-6">
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                  disabled={
                    isProcessing ||
                    !isBatchSaved ||
                    attachedClaims.length === 0 ||
                    files.some((f) => f.isUploading)
                  }
                  data-tooltip-id="submit-batch-tooltip"
                  data-tooltip-content={
                    !isBatchSaved
                      ? "Save batch header to submit"
                      : attachedClaims.length === 0
                      ? "Attach at least one claim to submit"
                      : "Submit batch"
                  }
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  Submit Batch
                </button>
              </div> */}
              {/* Save and Submit Buttons */}
              <div className="flex justify-end mt-6 gap-3">
                <button
                  onClick={handleSubmit_ForSavingOnly}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                  disabled={isProcessing || !isBatchSaved || !formData.batch_no}
                  data-tooltip-id="save-open-batch-tooltip"
                  data-tooltip-content="Save batch header with status Open"
                >
                  <FontAwesomeIcon icon={faSave} />
                  Save
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                  disabled={
                    isProcessing ||
                    !isBatchSaved ||
                    attachedClaims.length === 0 ||
                    files.some((f) => f.isUploading)
                  }
                  data-tooltip-id="submit-batch-tooltip"
                  data-tooltip-content={
                    !isBatchSaved
                      ? "Save batch header to submit"
                      : attachedClaims.length === 0
                      ? "Attach at least one claim to submit"
                      : "Submit batch"
                  }
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  Submit Batch
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Modal */}
        {isProcessing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 flex flex-col items-center space-y-4 animate-pulse">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-4xl text-indigo-600 animate-spin"
              />
              <p className="text-lg font-medium text-gray-800">
                {isProcessingText}
              </p>
              <p className="text-sm text-gray-500">
                This may take a moment. Please wait.
              </p>
            </div>
          </div>
        )}

        {/* Modals */}
        <ClaimDetailsModal
          isOpen={isClaimDetailsModalOpen}
          onClose={() => setIsClaimDetailsModalOpen(false)}
          claim={selectedClaim}
          phpFormatter={phpFormatter}
        />
        <AttachFilesModal
          isOpen={isAttachFilesModalOpen}
          onClose={() => setIsAttachFilesModalOpen(false)}
          claim={selectedClaimForFiles}
          files={claimFiles}
          setFiles={setClaimFiles}
          handleFileChange={handleClaimFileChange}
          handleRemoveFile={handleRemoveClaimFile}
          handleViewFile={handleViewClaimFile}
          handleSaveFiles={handleSaveClaimFiles}
          isDragging={isDraggingClaim}
          handleDragOver={handleClaimDragOver}
          handleDragLeave={handleClaimDragLeave}
          handleDrop={handleClaimDrop}
          handleChooseFile={handleChooseClaimFile}
          fileInputRef={claimFileInputRef}
          allowedFileTypes={allowedFileTypes}
          maxFileSizeMB={maxFileSizeMB}
          maxUploadLimit={maxUploadLimit}
        />
        <ConfirmSubmitModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleConfirmSubmit}
          formData={formData}
          soaFiles={files}
          attachedClaims={attachedClaims}
          phpFormatter={phpFormatter}
          isSubmitting={isProcessing}
        />
      </div>
    </div>
  );
};

export default AddClaimsBatch;
